const cities = [
  { name: "Stuart", zips: "34994, 34995, 34996, 34997" },
  { name: "Jensen Beach", zips: "34957, 34958" },
  { name: "Palm City", zips: "34990, 34991" },
  { name: "Hobe Sound", zips: "33455" },
  { name: "Port St. Lucie", zips: "34952, 34953, 34983, 34984, 34986, 34987" },
  { name: "Fort Pierce", zips: "34945, 34946, 34947, 34948, 34949, 34950, 34951, 34981, 34982" },
  { name: "Vero Beach", zips: "32960, 32961, 32962, 32963, 32964, 32965, 32966, 32967, 32968, 32969" },
  { name: "Jupiter", zips: "33458, 33468, 33469, 33477, 33478" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or the marina in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function StuartLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Stuart &amp; the Treasure Coast
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Stuart, Jensen Beach, Palm City, Hobe Sound, or anywhere along the Treasure Coast, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Stuart sits at the heart of Florida&apos;s Treasure Coast — a stretch of Atlantic shoreline defined by its connection to the water, its laid-back coastal culture, and a level of affluence that surprises first-time visitors. As the county seat of Martin County, Stuart is home to roughly 18,000 residents within city limits and anchors a broader coastal community that extends north to Fort Pierce, south to Jupiter, and west to the agricultural expanses near Indiantown. The city calls itself the &ldquo;Sailfish Capital of the World,&rdquo; and while that title is more marketing than science, the fishing culture here is real — charter boats line the docks at the Stuart Causeway, the annual Stuart Sailfish Classic draws anglers from across the country, and the Indian River Lagoon remains one of the most productive inshore fisheries in the United States.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What distinguishes Stuart from other Florida coastal towns is its deliberate resistance to overdevelopment. Martin County has maintained some of the strictest growth management policies in the state, preserving natural shorelines, limiting high-rise construction, and protecting the St. Lucie River and Indian River Lagoon ecosystems. The result is a community that feels more like coastal New England than South Florida — walkable downtown streets lined with local boutiques and waterfront restaurants, historic homes with wide front porches, and a pace of life that prioritizes fishing, boating, and beach time over nightlife and crowds. The Lyric Theatre, a 1926 movie house converted to a performing arts venue, anchors the cultural scene. The Florida Oceanographic Coastal Center draws families and school groups to its interactive marine exhibits.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Stuart&apos;s residential landscape reflects its coastal identity and its retiree appeal. Hutchinson Island — a narrow barrier beach separating the Indian River Lagoon from the Atlantic — hosts some of the most desirable oceanfront properties on the Treasure Coast, including the private Sailfish Point community with its Arnold Palmer-designed golf course and full-service marina. Back on the mainland, Harbour Ridge and Willoughby Golf Club cater to active retirees who want country club living without the Palm Beach price tag. Palm City, just west of Stuart, offers newer suburban developments with excellent schools — Martin County High, South Fork High, and Jensen Beach High consistently rank among the best in the region. Hobe Sound, to the south, retains a quieter, more natural character with Jonathan Dickinson State Park and the Hobe Sound National Wildlife Refuge.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The travel demographics of Stuart and the Treasure Coast differ from those of South Florida in meaningful ways. Snowbirds and retirees make up a significant portion of the seasonal population, many maintaining northern homes in New York, New Jersey, Pennsylvania, or Canada and migrating to Stuart for the winter months. These seasonal residents often discover passport issues when transitioning between homes — their passport expired during the summer up north, and they only realize it when booking a Caribbean cruise or a spring trip to Europe. The angling and boating community travels to the Bahamas, Costa Rica, and Mexico for sportfishing expeditions, frequently booking on compressed timelines when tournament dates are announced or charter availability opens. Young families in Palm City and Port St. Lucie travel to Disney cruises from Port Canaveral, the Caribbean for spring break, and Central America for adventure vacations.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Cleveland Clinic Martin Health anchors the medical infrastructure of the region, drawing physicians, nurses, and specialists from across the country. Medical professionals relocating to Stuart for work at Cleveland Clinic often discover that their passports expired during their previous residency or fellowship — a discovery that becomes urgent when they are invited to present at a medical conference in Europe or attend training abroad. The nursing and physician assistant programs at Indian River State College also send students on international rotations and medical mission trips that require current passports.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            The geographic reality of Stuart matters for passport processing. The closest regional passport agency is in Miami, approximately 120 miles south via the Florida Turnpike and I-95 — a drive that consumes over two hours even in good traffic. For a Stuart angler who discovers an expired passport the week before a Bahamas fishing tournament, or a snowbird who realizes their passport expired while they were up north for the summer, that drive is not just inconvenient — it may be impossible. Fast Passport Center has processed expedited passports for Stuart travelers across every demographic: retirees, anglers, medical professionals, young families, and snowbirds. Our courier-based expedited service means you start online from your Hutchinson Island condo, your Palm City home, or your downtown Stuart office — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Stuart — approximately 70 miles south via I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Martin &amp; St. Lucie counties</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or Stuart marina in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Martin, St. Lucie, or northern Palm Beach County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Jupiter", href: "/cities/jupiter" },
                  { name: "Palm Beach Gardens", href: "/cities/palm-beach-gardens" },
                  { name: "West Palm Beach", href: "/cities/west-palm-beach" },
                  { name: "Jensen Beach", href: "/cities/jensen-beach" },
                  { name: "Port St. Lucie", href: "/cities/port-st-lucie" },
                  { name: "Wellington", href: "/cities/wellington" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}